import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WRAP from 'WRAP-JS';
import FIR from "./layers/FIR";
import Airfield from "./layers/Airfield";
import LonLat from "./layers/LonLat";
import { convertWRAPPoint, formatLonLatDM, lazyIdle } from "../../utils/FPEUtils";
import Fix from "./layers/Fix";
import Airway from "./layers/Airway";
import useEffectSkipFirst from "../../hooks/useEffectSkipFirst";
import TileMap from "./layers/TileMap";
import Vaa from "./layers/Vaa";
import Msm from "./layers/Msm";
import Lfm from "./layers/Lfm";
import Gsm from "./layers/Gsm"
import Himawari from "./layers/Himawari";
import Radar from "./layers/Radar";
import TropicalCyclone from "./layers/TropicalCyclone";
import MetarTaf from "./layers/MetarTaf";
import { useRecoilValue } from "recoil";
import { useMapColorState } from "../../store/MapColorState";
import { mapPerspectiveState } from "../../store/MapPerspectiveState";
import ContentsDispatcher from "./dispatch/ContentsDispatcher.js";
import WxContentsDispatcher from "./dispatch/WxContentsDispatcher";
import {
  WX_ID_ACOS,
  WX_ID_AMEDAS,
  WX_ID_AMEYUKI,
  WX_ID_GASS_SCALE,
  WX_ID_HIMAWARI,
  WX_ID_LFM,
  WX_ID_LIGHTNING,
  WX_ID_LIVECAMERA,
  WX_ID_METARTAF,
  WX_ID_MSM,
  WX_ID_POTEKA,
  WX_ID_RADAR,
  WX_ID_SS_SCALE,
  WX_ID_SYNOP,
  WX_ID_SST,
  WX_ID_TIME_SCOPE_COMPOSITE_RADAR,
  WX_ID_TROPICALCYCLONE,
  WX_ID_VAA,
  WX_SETTINGS,
  WX_ID_GSM,
  WX_ID_GFS,
  WX_ID_WNI_OCEAN_CURRENT,
  WX_ID_SORACAMELIGHT
} from "../../store/WeatherContentsState";
import {
  MAP_ID_AIRPORT,
  MAP_ID_AIRWAY,
  MAP_ID_COASTLINE,
  MAP_ID_CONTROL_ZONE,
  MAP_ID_COPILOT,
  MAP_ID_CUSTOM_POINT_AREA,
  MAP_ID_ELEVATION,
  MAP_ID_FIR,
  MAP_ID_FIX,
  MAP_ID_FLIGHT_HISTORY,
  MAP_ID_FLIGHTPLAN_LIST,
  MAP_ID_INFORMATION_ZONE,
  MAP_ID_LONLAT,
  MAP_ID_NAVAIDS,
  MAP_ID_TILEMAP,
  MAP_ID_TRAINING_AIRSPACE,
  MAP_ID_POWER_LINES,
  MAP_ID_POWER_PYLONS, MAP_ID_FORECAST_REGION
} from "../../store/MapContentsState";
import FlightPlanListDispatcher from "./dispatch/FlightPlanListDispatcher";
import Navaids from "./layers/Navaids";
import CoastLine from "./layers/CoastLine";
import { getCustomerSettings } from "../../CustomerSettings";
import Tooltip from "./helper/Tooltip";
import { routePointEditingState } from '../../store/RoutePointEditingState';
import RadarDispatcher from "./dispatch/RadarDispatcher";
import MetarTafDispatcher from "./dispatch/MetarTafDispatcher";
import Lightning from "./layers/Lightning";
import CoastLineDispatcher from './dispatch/CoastLineDispatcher';
import LiveCamera from "./layers/LiveCamera";
import Elevation from './layers/Elevation';
import Amedas from "./layers/Amedas";
import Synop from "./layers/Synop";
import WebGLMap from "./engine/WebGLMap";
import GoogleMap from "./engine/GoogleMap";
import { getZoomRange } from "./helper/MapUtil";
import TileMapDispatcher from "./dispatch/TileMapDispatcher";
import HimawariDispatcher from "./dispatch/HimawariDispatcher";
import AmedasDispatcher from "./dispatch/AmedasDispatcher";
import Poteka from './layers/Poteka';
import PotekaDispatcher from './dispatch/PotekaDispatcher';
import { useTranslation } from 'react-i18next';
import { timeZoneSelector } from '../../store/TimeZoneState';
import FlightPlanList from './layers/FlightPlanList';
import Ameyuki from './layers/Ameyuki';
import SsScale from './layers/SsScale';
import GassScale from './layers/GassScale';
import { Loading } from "../../parts/Loading";
// import AcosStatusDB from './layers/AcosStatusDB';
import CoPilot from "./layers/Copilot";
import CoPilotDispatcher from './dispatch/CopilotDispatcher';
import { historyModeState } from '../../store/HistoryModeState';
import clsx from 'clsx';
import FlightHistoryDispatcher from './dispatch/FlightHistoryDispatcher';
import FlightHistory from './layers/FlightHistory';
import LiveCameraDispatcher from "./dispatch/LiveCameraDispatcher";
import PointArea from './layers/PointArea';
import PointAreaDispatcher from './dispatch/PointAreaDispatcher';
import MapScaleMeasure from "./MapScaleMeasure";
import Acos from './layers/Acos';
import ControlZone from './layers/ControlZone';
import InformationZone from './layers/InformationZone';
import TrainingAirspace from './layers/TrainingAirspace';
import ExWxContentsDispatcher from './dispatch/ExWxContentsDispatcher';
import { userState } from '../../store/UserState';
import PowerLine from './layers/PowerLine';
import { GOOGLE_MAP_THEME, useGoogleMap } from '../../store/GoogleMapState';
import PowerPylon from './layers/PowerPylon';
import { useDisabledContents } from '../../store/DisabledContentsState';
import ForecastRegion from "./layers/ForecastRegion";
import SelectCsSource from '../copilot/SelectCsSource';
import SeasonComment from './SeasonComment';
import TimeScopeCompositeRadar from "./layers/TimeScopeCompositeRadar";
import MapPointInfoDialog from "./MapPointInfoDialog";
import Gfs from './layers/Gfs.js';
import WniOceanCurrent from './layers/WniOceanCurrent.js';
import SoraCame from "./layers/SoraCame";
import Sst from './layers/Sst.js';
import ModelDispatcher from "./dispatch/ModelDispatcher";
import ElevationDispatcher from "./dispatch/ElevationDispatcher";
const ContentsMenuTab  = lazyIdle(() => import("./menu/ContentsMenuTab"));
const ContentsInfoPanel = lazyIdle(() => import("./ContentsInfoPanel"));
const BackgroundLegendObserver = lazyIdle(() => import("./legend/BackgroundLegendObserver"));

export const TOOLTIP_GROUP_1 = 'TOOLTIP_GROUP_1'; //Himawari cloud top, TrainingAirspace

function Map() {

  const {mapColor} = useMapColorState();
  const {themeName} = useGoogleMap();
  const mapPerspective = useRecoilValue(mapPerspectiveState);
  const layerConfig = getCustomerSettings('layerConfig');
  const routePointEditing = useRecoilValue(routePointEditingState);
  const [t, i18n] = useTranslation();
  const timeZone = useRecoilValue(timeZoneSelector);
  const historyMode = useRecoilValue(historyModeState);
  const user = useRecoilValue(userState);
  const [mapEngineReady, setMapEngineReady] = useState(false);
  const {disabled} = useDisabledContents();

  useEffect(() => {
    initMap();
    // 初回render時のみ実行するので警告無視
    // eslint-disable-next-line
  },[]);

  const isFirstRender = useRef(true);
  if (isFirstRender.current) {
    isFirstRender.current = false;
    initMapContents(mapColor, themeName, [0,530], layerConfig, user, disabled);
  }

  useEffect(() => {
    adjustCenterPosition(mapPerspective.centerLat, mapPerspective.centerLon, mapPerspective.zoom);
  }, [mapPerspective]);

  const dependency = useMemo(() => ([mapColor, themeName]), [mapColor, themeName]);
  useEffectSkipFirst(() => {
    applyMapColor(mapColor, themeName);
  }, dependency);

  useEffect(() => {
    changeRoutePointEditing(routePointEditing);
  }, [routePointEditing]);

  useEffect(() => {
    changeLanguage(i18n.language, t);
  }, [t, i18n]);

  useEffect(() => {
    changeTimeZone(timeZone);
  }, [timeZone]);

  const handleMapEngineReady = useCallback(() => {
    setMapEngineReady(true);
  },[]);

  return (
    <>
      <div id="map-container" style={{width:'100%',height:"100%",position:'relative'}} className={clsx(historyMode && 'history')}>
        <WebGLMap onReady={handleMapEngineReady}/>
        <GoogleMap onReady={handleMapEngineReady} mapPerspective={mapPerspective}/>
      </div>
      <div id="map-tooltip" className="map-tooltip"/>
      <GsiCopyright/>
      <MapScaleMeasure/>
      <MapGadget />
      <MapPointInfoDialog />
      <SeasonComment />
      {!mapEngineReady && <Loading backdrop={true}/>}
      {mapEngineReady &&
        <>
          {mapContents[MAP_ID_TILEMAP] && <TileMapDispatcher contentsID={MAP_ID_TILEMAP} contents={mapContents[MAP_ID_TILEMAP]}/>}
          <Suspense fallback={""}>
            <ContentsMenuTab mapContents={mapContents}/>
          </Suspense>
          <Suspense fallback={""}>
            <ContentsInfoPanel/>
          </Suspense>
          <Suspense fallback={""}>
            <BackgroundLegendObserver/>
          </Suspense>
          {/*<SigmetUnreadPositionPanel/>*/}
          <Suspense fallback={<Loading backdrop={true}/>}>
            {mapContents[MAP_ID_FLIGHTPLAN_LIST] && <FlightPlanListDispatcher contentsID={MAP_ID_FLIGHTPLAN_LIST} contents={mapContents[MAP_ID_FLIGHTPLAN_LIST]}/>}
            {/*{mapContents[WX_ID_SIGMET] && <SigmetDispatcher contentsID={WX_ID_SIGMET} contents={mapContents[WX_ID_SIGMET]} altType={WX_SETTINGS[WX_ID_SIGMET].altitude}/>}*/}
            {mapContents[WX_ID_RADAR] && <RadarDispatcher contentsID={WX_ID_RADAR} contents={mapContents[WX_ID_RADAR]} altType={WX_SETTINGS[WX_ID_RADAR].altitude}/>}
            {mapContents[WX_ID_VAA] &&<WxContentsDispatcher contentsID={WX_ID_VAA} contents={mapContents[WX_ID_VAA]} altType={WX_SETTINGS[WX_ID_VAA].altitude}/>}
            {/*{mapContents[WX_ID_SIGWX] && <WxContentsDispatcher contentsID={WX_ID_SIGWX} contents={mapContents[WX_ID_SIGWX]} altType={WX_SETTINGS[WX_ID_SIGWX].altitude}/>}*/}
            {/*{mapContents[WX_ID_UKMET] && <WxContentsDispatcher contentsID={WX_ID_UKMET} contents={mapContents[WX_ID_UKMET]} altType={WX_SETTINGS[WX_ID_UKMET].altitude}/>}*/}
            {mapContents[WX_ID_MSM] && <ModelDispatcher contentsID={WX_ID_MSM} contents={mapContents[WX_ID_MSM]} altType={WX_SETTINGS[WX_ID_MSM].altitude}/>}
            {mapContents[WX_ID_LFM] && <ModelDispatcher contentsID={WX_ID_LFM} contents={mapContents[WX_ID_LFM]} altType={WX_SETTINGS[WX_ID_LFM].altitude}/>}
            {mapContents[WX_ID_GSM] && <ModelDispatcher contentsID={WX_ID_GSM} contents={mapContents[WX_ID_GSM]} altType={WX_SETTINGS[WX_ID_GSM].altitude}/>}
            {mapContents[WX_ID_GFS] && <ModelDispatcher contentsID={WX_ID_GFS} contents={mapContents[WX_ID_GFS]} altType={WX_SETTINGS[WX_ID_GFS].altitude}/>}
            {mapContents[WX_ID_WNI_OCEAN_CURRENT] && <WxContentsDispatcher contentsID={WX_ID_WNI_OCEAN_CURRENT} contents={mapContents[WX_ID_WNI_OCEAN_CURRENT]} altType={WX_SETTINGS[WX_ID_WNI_OCEAN_CURRENT].altitude}/>}
            {/*{mapContents[WX_ID_SATELLITE] && <WxContentsDispatcher contentsID={WX_ID_SATELLITE} contents={mapContents[WX_ID_SATELLITE]} altType={WX_SETTINGS[WX_ID_SATELLITE].altitude}/>}*/}
            {mapContents[WX_ID_HIMAWARI] && <HimawariDispatcher contentsID={WX_ID_HIMAWARI} contents={mapContents[WX_ID_HIMAWARI]} altType={WX_SETTINGS[WX_ID_HIMAWARI].altitude}/>}
            {/*{mapContents[WX_ID_ASCSCALE] && <WxContentsDispatcher contentsID={WX_ID_ASCSCALE} contents={mapContents[WX_ID_ASCSCALE]} altType={WX_SETTINGS[WX_ID_ASCSCALE].altitude}/>}*/}
            {mapContents[WX_ID_ACOS] && <ExWxContentsDispatcher contentsID={WX_ID_ACOS} contents={mapContents[WX_ID_ACOS]} altType={WX_SETTINGS[WX_ID_ACOS].altitude}/>}
            {/*{mapContents[WX_ID_SURFACEANALYSIS] && <WxContentsDispatcher contentsID={WX_ID_SURFACEANALYSIS} contents={mapContents[WX_ID_SURFACEANALYSIS]} altType={WX_SETTINGS[WX_ID_SURFACEANALYSIS].altitude}/>}*/}
            {mapContents[MAP_ID_INFORMATION_ZONE] && <ContentsDispatcher contentsID={MAP_ID_INFORMATION_ZONE} contents={mapContents[MAP_ID_INFORMATION_ZONE]}/>}
            {mapContents[MAP_ID_CONTROL_ZONE] && <ContentsDispatcher contentsID={MAP_ID_CONTROL_ZONE} contents={mapContents[MAP_ID_CONTROL_ZONE]}/>}
            {mapContents[MAP_ID_TRAINING_AIRSPACE] && <ContentsDispatcher contentsID={MAP_ID_TRAINING_AIRSPACE} contents={mapContents[MAP_ID_TRAINING_AIRSPACE]}/>}
            {mapContents[MAP_ID_CUSTOM_POINT_AREA] && <PointAreaDispatcher contentsID={MAP_ID_CUSTOM_POINT_AREA} contents={mapContents[MAP_ID_CUSTOM_POINT_AREA]}/>}
            {mapContents[WX_ID_TROPICALCYCLONE] && <ExWxContentsDispatcher contentsID={WX_ID_TROPICALCYCLONE} contents={mapContents[WX_ID_TROPICALCYCLONE]} altType={WX_SETTINGS[WX_ID_TROPICALCYCLONE].altitude}/>}
            {mapContents[WX_ID_LIGHTNING] && <WxContentsDispatcher contentsID={WX_ID_LIGHTNING} contents={mapContents[WX_ID_LIGHTNING]} altType={WX_SETTINGS[WX_ID_LIGHTNING].altitude}/>}
            {mapContents[WX_ID_AMEDAS] && <AmedasDispatcher contentsID={WX_ID_AMEDAS} contents={mapContents[WX_ID_AMEDAS]} altType={WX_SETTINGS[WX_ID_AMEDAS].altitude}/>}
            {mapContents[WX_ID_SYNOP] && <WxContentsDispatcher contentsID={WX_ID_SYNOP} contents={mapContents[WX_ID_SYNOP]} altType={WX_SETTINGS[WX_ID_SYNOP].altitude}/>}
            {mapContents[WX_ID_SST] && <WxContentsDispatcher contentsID={WX_ID_SST} contents={mapContents[WX_ID_SST]} altType={WX_SETTINGS[WX_ID_SST].altitude}/>}
            {mapContents[WX_ID_METARTAF] && <MetarTafDispatcher contentsID={WX_ID_METARTAF} contents={mapContents[WX_ID_METARTAF]} altType={WX_SETTINGS[WX_ID_METARTAF].altitude}/>}
            {mapContents[WX_ID_LIVECAMERA] && <LiveCameraDispatcher contentsID={WX_ID_LIVECAMERA} contents={mapContents[WX_ID_LIVECAMERA]} altType={WX_SETTINGS[WX_ID_LIVECAMERA].altitude}/>}
            {mapContents[WX_ID_POTEKA] && <PotekaDispatcher contentsID={WX_ID_POTEKA} contents={mapContents[WX_ID_POTEKA]} altType={WX_SETTINGS[WX_ID_POTEKA].altitude}/>}
            {mapContents[WX_ID_AMEYUKI] && <WxContentsDispatcher contentsID={WX_ID_AMEYUKI} contents={mapContents[WX_ID_AMEYUKI]} altType={WX_SETTINGS[WX_ID_AMEYUKI].altitude}/>}
            {mapContents[WX_ID_SS_SCALE] && <WxContentsDispatcher contentsID={WX_ID_SS_SCALE} contents={mapContents[WX_ID_SS_SCALE]} altType={WX_SETTINGS[WX_ID_SS_SCALE].altitude}/>}
            {mapContents[WX_ID_GASS_SCALE] && <WxContentsDispatcher contentsID={WX_ID_GASS_SCALE} contents={mapContents[WX_ID_GASS_SCALE]} altType={WX_SETTINGS[WX_ID_GASS_SCALE].altitude}/>}
            {mapContents[WX_ID_TIME_SCOPE_COMPOSITE_RADAR] && <WxContentsDispatcher contentsID={WX_ID_TIME_SCOPE_COMPOSITE_RADAR} contents={mapContents[WX_ID_TIME_SCOPE_COMPOSITE_RADAR]} altType={WX_SETTINGS[WX_ID_TIME_SCOPE_COMPOSITE_RADAR].altitude}/>}
            {mapContents[MAP_ID_LONLAT] && <ContentsDispatcher contentsID={MAP_ID_LONLAT} contents={mapContents[MAP_ID_LONLAT]}/>}
            {mapContents[MAP_ID_FIR] && <ContentsDispatcher contentsID={MAP_ID_FIR} contents={mapContents[MAP_ID_FIR]}/>}
            {mapContents[MAP_ID_AIRPORT] && <ContentsDispatcher contentsID={MAP_ID_AIRPORT} contents={mapContents[MAP_ID_AIRPORT]}/>}
            {mapContents[MAP_ID_FIX] && <ContentsDispatcher contentsID={MAP_ID_FIX} contents={mapContents[MAP_ID_FIX]}/>}
            {mapContents[MAP_ID_AIRWAY] && <ContentsDispatcher contentsID={MAP_ID_AIRWAY} contents={mapContents[MAP_ID_AIRWAY]}/>}
            {mapContents[MAP_ID_NAVAIDS] && <ContentsDispatcher contentsID={MAP_ID_NAVAIDS} contents={mapContents[MAP_ID_NAVAIDS]}/>}
            {mapContents[MAP_ID_COASTLINE] && <CoastLineDispatcher contentsID={MAP_ID_COASTLINE} contents={mapContents[MAP_ID_COASTLINE]}/>}
            {mapContents[MAP_ID_ELEVATION] && <ElevationDispatcher contentsID={MAP_ID_ELEVATION} contents={mapContents[MAP_ID_ELEVATION]}/>}
            {mapContents[MAP_ID_COPILOT] && <CoPilotDispatcher contentsID={MAP_ID_COPILOT} contents={mapContents[MAP_ID_COPILOT]}/>}
            {mapContents[MAP_ID_FLIGHT_HISTORY] && <FlightHistoryDispatcher contentsID={MAP_ID_FLIGHT_HISTORY} contents={mapContents[MAP_ID_FLIGHT_HISTORY]}/>}
            {mapContents[MAP_ID_POWER_LINES] && <ContentsDispatcher contentsID={MAP_ID_POWER_LINES} contents={mapContents[MAP_ID_POWER_LINES]} />}
            {mapContents[MAP_ID_POWER_PYLONS] && <ContentsDispatcher contentsID={MAP_ID_POWER_PYLONS} contents={mapContents[MAP_ID_POWER_PYLONS]} />}
            {mapContents[MAP_ID_FORECAST_REGION] && <ContentsDispatcher contentsID={MAP_ID_FORECAST_REGION} contents={mapContents[MAP_ID_FORECAST_REGION]} />}
            {mapContents[WX_ID_SORACAMELIGHT] && <WxContentsDispatcher contentsID={WX_ID_SORACAMELIGHT} contents={mapContents[WX_ID_SORACAMELIGHT]} altType={WX_SETTINGS[WX_ID_SORACAMELIGHT].altitude}/>}
          </Suspense>
          <SelectCsSource/>
        </>
      }
    </>
  );
}

export default Map;

const initMap = () => {
  initLayer();

  initInteraction();
}

let mapContents = {};

const initMapContents = (mapColor, themeName, altitude, layerConfig, user, disabled) => {

  const factory = (id, clz, ...arg) => {
    if (layerConfig[id]) {
      mapContents[id] = new clz(...arg);
    }
  }

  const isDark = isDarkTone(mapColor, themeName);

  factory(MAP_ID_TILEMAP, TileMap, mapColor);
  factory(MAP_ID_COASTLINE, CoastLine, isDark);
  factory(MAP_ID_LONLAT, LonLat);
  factory(MAP_ID_FLIGHTPLAN_LIST, FlightPlanList, isDark, user.group);
  factory(MAP_ID_FIR, FIR);
  factory(MAP_ID_AIRPORT, Airfield, isDark);
  factory(MAP_ID_AIRWAY, Airway);
  factory(MAP_ID_FIX, Fix);
  factory(MAP_ID_NAVAIDS, Navaids);
  // factory(WX_ID_SIGMET, Sigmet, altitude);
  factory(WX_ID_VAA, Vaa, altitude);
  // factory(WX_ID_SIGWX, Sigwx, altitude);
  // factory(WX_ID_UKMET, Ukmet);
  factory(WX_ID_MSM, Msm, isDark);
  factory(WX_ID_LFM, Lfm, isDark);
  factory(WX_ID_GSM, Gsm, isDark);
  factory(WX_ID_GFS, Gfs, isDark);
  factory(WX_ID_WNI_OCEAN_CURRENT, WniOceanCurrent, isDark);
  // factory(WX_ID_SATELLITE, Satellite);
  factory(WX_ID_HIMAWARI, Himawari);
  // factory(WX_ID_ASCSCALE, AscScale);
  // factory(WX_ID_ACOS, AcosStatusDB, isDark);
  factory(WX_ID_ACOS, Acos, isDark);
  factory(WX_ID_RADAR, Radar, layerConfig[WX_ID_RADAR]);
  factory(WX_ID_TIME_SCOPE_COMPOSITE_RADAR, TimeScopeCompositeRadar)
  // factory(WX_ID_SURFACEANALYSIS, SurfaceAnalysis);
  factory(MAP_ID_INFORMATION_ZONE, InformationZone);
  factory(MAP_ID_CONTROL_ZONE, ControlZone);
  factory(MAP_ID_TRAINING_AIRSPACE, TrainingAirspace);
  factory(MAP_ID_CUSTOM_POINT_AREA, PointArea);
  factory(WX_ID_TROPICALCYCLONE, TropicalCyclone);
  factory(WX_ID_METARTAF, MetarTaf, disabled);
  factory(WX_ID_LIGHTNING, Lightning);
  factory(WX_ID_AMEDAS, Amedas, layerConfig[WX_ID_AMEDAS]);
  factory(WX_ID_SYNOP, Synop, layerConfig[WX_ID_SYNOP]);
  factory(WX_ID_SST, Sst, layerConfig[WX_ID_SST]);
  factory(WX_ID_LIVECAMERA, LiveCamera, user.contract);
  factory(MAP_ID_ELEVATION, Elevation);
  factory(WX_ID_POTEKA, Poteka, layerConfig[WX_ID_POTEKA]);
  factory(WX_ID_AMEYUKI, Ameyuki, layerConfig[WX_ID_AMEYUKI]);
  factory(WX_ID_SS_SCALE, SsScale, layerConfig[WX_ID_SS_SCALE]);
  factory(WX_ID_GASS_SCALE, GassScale, layerConfig[WX_ID_GASS_SCALE]);
  factory(MAP_ID_COPILOT, CoPilot);
  factory(MAP_ID_FLIGHT_HISTORY, FlightHistory);
  factory(MAP_ID_POWER_LINES, PowerLine, disabled);
  factory(MAP_ID_POWER_PYLONS, PowerPylon, disabled, isDark);
  factory(MAP_ID_FORECAST_REGION, ForecastRegion, isDark);
  factory(WX_ID_SORACAMELIGHT, SoraCame);
};

const initLayer = () => {
  const layerOrder = getCustomerSettings('layerOrder');

  let contentsLayers = layerOrder.reduce((list, contentsId) => {
    if (mapContents[contentsId]) {
      list.push(mapContents[contentsId].getLayer());
    }
    return list;
  },[]);

  const layers = contentsLayers.flat().reverse();
  WRAP.Geo.setLayer(layers);
}

const initInteraction = () => {
  WRAP.Geo.setInteraction(document.getElementById('map-container'));

  const tooltipGroups = {};
  for(const key in mapContents) {
    const contents = mapContents[key];
    if(typeof contents.initInteraction === 'function') {
      contents.initInteraction();
    }
    if(typeof contents.getTooltipGroup === 'function') {
      const tooltipGroup = contents.getTooltipGroup();
      if (!tooltipGroups[tooltipGroup.id]) {
        tooltipGroups[tooltipGroup.id] = [];
      }
      tooltipGroups[tooltipGroup.id].push(...tooltipGroup.layers);
    }
  }
  Object.values(tooltipGroups).forEach(layers => {
    WRAP.Geo.addTooltipGroup(layers);
  });

  WRAP.Geo.addEventHandler('boundsChange', updateBounds);
  WRAP.Geo.addEventHandler('mapDragStart', mapDragStart);
  WRAP.Geo.addEventHandler('mapDragEnd', mapDragEnd);
  window.addEventListener('resize', windowResize);

  [
    document.getElementById('webgl-map'),
    document.getElementById('google-map')
  ].forEach(mapDiv => {
    mapDiv.addEventListener('mousemove', displayMousePoint);
    const tooltip = new Tooltip(mapDiv, 'map-tooltip');
    tooltip.addLayer(mapContents[MAP_ID_FIR]);
  });

};

const adjustCenterPosition = (lat, lon, zoom) => {
  const center = convertWRAPPoint(lat, lon);
  if (zoom < WRAP.Geo.currentView().min_zoom) {
    WRAP.Geo.setZoomRange(zoom, WRAP.Geo.currentView().max_zoom);
  }
  WRAP.Geo.setCenterPoint(center);
  if (!zoom) zoom = WRAP.Geo.getZoom();
  WRAP.Geo.setZoom(zoom)
};

const displayMousePoint = event => {
  const rect = event.currentTarget.getBoundingClientRect();
  const pt = WRAP.Geo.getPoint(new WRAP.Geo.ScreenPoint(event.clientX - rect.left, event.clientY - rect.top));
  document.getElementById('mouse-point').innerText = formatLonLatDM(pt.lonDegree(), pt.latDegree());
};

let prevZoom;
const changeZoomLevel = () => {
  const zoom = getZoomRange();

  if (WRAP.Geo.currentView().map.div === undefined) {
    //GoogleMap
    zoom.min_zoom = Math.floor(zoom.min_zoom);
  }

  if (!prevZoom || (prevZoom.min_zoom !== zoom.min_zoom || prevZoom.max_zoom !== zoom.max_zoom)) {
    WRAP.Geo.setZoomRange(zoom.min_zoom, zoom.max_zoom);
    prevZoom = zoom;
  }

  const lv = WRAP.Geo.getZoom();
  const zoomLevel = document.getElementById('zoom-level')
  if (zoomLevel) {
    const digit = Math.pow(10, 2);
    zoomLevel.innerText = `Zoom: ${Math.floor(lv * digit)/digit}`;
  }
}

let resizeTimer;
const windowResize = () => {
  if (resizeTimer) {
    clearTimeout(resizeTimer);
  }
  resizeTimer = setTimeout(function () {
    if (!WRAP.Geo.view.map || !WRAP.Geo.view.map.ready()) {
      return;
    }
    changeZoomLevel();
  }, 300);
};

const broadcastMessage = (message, ...args) => {
  for (let mapContentsKey in mapContents) {
    mapContents[mapContentsKey][message] && mapContents[mapContentsKey][message](...args);
  }
}

let updateBoundsTimer;
const updateBounds = () => {
  if (updateBoundsTimer) {
    clearTimeout(updateBoundsTimer);
  }
  updateBoundsTimer = setTimeout(() => {
    if (!WRAP.Geo.view.map.ready()) {
      return;
    }
    changeZoomLevel();
    broadcastMessage('updateBounds');
  }, 300);
};

const isDarkTone = (mapColor, themeName) => {
  return mapColor !== 'google' ? mapColor === "black"
    : [GOOGLE_MAP_THEME.DARK, GOOGLE_MAP_THEME.DARK2, GOOGLE_MAP_THEME.DARK3, GOOGLE_MAP_THEME.SATELLITE].includes(themeName);
};

const applyMapColor = (mapColor, themeName) => {
  const isDark = isDarkTone(mapColor, themeName);
  broadcastMessage('applyMapColor', mapColor, isDark);
};

const mapDragStart = () => {
  document.getElementById('map-wrap').style.userSelect = 'none';
}

const mapDragEnd = () => {
  document.getElementById('map-wrap').style.userSelect = 'auto';
}

const changeRoutePointEditing = routePointEditing => {
  broadcastMessage('changeRoutePointEditing', routePointEditing);
}

const changeLanguage = (...args) => {
  broadcastMessage('changeLanguage', ...args);
}

const changeTimeZone = (...args) => {
  broadcastMessage('changeTimeZone', ...args);
}

export const getMapContents = () => mapContents;

const GsiCopyright = () => {
  const {isGsiMap, isLightMap} =useMapColorState();
  const hidden = !(isGsiMap || isLightMap);
  return (
    <a id="kokudo-chiliin" href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank" rel="noreferrer noopener" hidden={hidden}>地理院タイル</a>
  )
};

const MapGadget = () => {
  return (
    <div id="gadget-container">
      <div id="mouse-point" />
      <div id="zoom-level" />
      <div id="elevation">
        <div>
          <div className="title">ELV: </div>
          <div>
            <span className="value"></span>
            <span> ft</span>
          </div>
        </div>
      </div>
    </div>
  );
};
